import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import "./ContactSection.scss";

import TextImg from "../../img/funtler-text-img.svg";
import Input from "../Input/Input";
import ThemeBtn from "../ThemeBtn/ThemeBtn";
import FetchService from "../../api/FetchService";

const RECAPTCHA_SITE_KEY = "6Ldqj8stAAAAACfoN-g7TCFlTZh0vM69bkD4h6m1";
let recaptchaLoaderPromise = null;

const loadRecaptcha = () => {
  if (typeof window === "undefined") {
    return Promise.resolve(false);
  }
  if (window.grecaptcha && window.grecaptcha.render) {
    return Promise.resolve(true);
  }
  if (recaptchaLoaderPromise) {
    return recaptchaLoaderPromise;
  }
  recaptchaLoaderPromise = new Promise((resolve) => {
    let settled = false;
    const finish = (loaded) => {
      if (settled) return;
      settled = true;
      resolve(loaded);
    };
    const existingScript = document.querySelector('script[data-funtlers-recaptcha="true"]');
    if (existingScript) {
      existingScript.addEventListener("load", () => finish(true));
      existingScript.addEventListener("error", () => finish(false));
    } else {
      const script = document.createElement("script");
      script.src = "https://www.google.com/recaptcha/api.js";
      script.async = true;
      script.defer = true;
      script.setAttribute("data-funtlers-recaptcha", "true");
      script.addEventListener("load", () => finish(true));
      script.addEventListener("error", () => finish(false));
      document.head.appendChild(script);
    }
    setTimeout(() => finish(Boolean(window.grecaptcha && window.grecaptcha.render)), 10000);
  });
  return recaptchaLoaderPromise;
};

function validateEmail(email) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
  return emailRegex.test(email.trim());
}

function ContactSection() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [subject, setSubject] = useState("");
  const [sending, setSending] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const [recaptchaCompleted, setRecaptchaCompleted] = useState(false);
  const recaptchaWidgetRef = useRef(null);

  useEffect(() => {
    const renderCaptcha = async () => {
      const isLoaded = await loadRecaptcha();
      if (!isLoaded || !window.grecaptcha || !recaptchaWidgetRef.current) return;
      if (recaptchaWidgetRef.current.dataset.widgetId) return;
      const widgetId = window.grecaptcha.render(recaptchaWidgetRef.current, {
        sitekey: RECAPTCHA_SITE_KEY,
        callback: () => {
          setErrorMessage("");
          setRecaptchaCompleted(true);
        },
        "expired-callback": () => {
          setErrorMessage("reCAPTCHA-expirasjonen gikk ut. Vennligst bekreft igjen.");
          setRecaptchaCompleted(false);
        }
      });
      recaptchaWidgetRef.current.dataset.widgetId = widgetId;
      setRecaptchaReady(true);
    };
    renderCaptcha();
  }, []);

  const getRecaptchaToken = async () => {
    const isLoaded = await loadRecaptcha();
    if (!isLoaded || !window.grecaptcha || !recaptchaWidgetRef.current) {
      throw new Error("The reCAPTCHA widget could not be loaded.");
    }
    const token = window.grecaptcha.getResponse();
    if (!token || typeof token !== "string" || token.length === 0) {
      throw new Error("Du må bekrefte at du ikke er en robot før du sender skjemaet.");
    }
    return token;
  };

  const isFormValid = () => {
    return (
      name.trim().length > 1 &&
      validateEmail(email) &&
      subject.trim().length > 1 &&
      message.trim().length > 5 &&
      recaptchaCompleted &&
      recaptchaReady &&
      !sending
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");
    setSending(true);
    let recaptchaToken;
    // Frontend validation
    if (!name.trim() || !validateEmail(email) || !subject.trim() || message.trim().length < 6) {
      setErrorMessage("Vennligst fyll ut alle feltene korrekt.");
      setSending(false);
      return;
    }
    try {
      recaptchaToken = await getRecaptchaToken();
    } catch (error) {
      setErrorMessage(error.message || "Du må bekrefte at du ikke er en robot før du sender skjemaet.");
      setSending(false);
      return;
    }
    const request = {
      name: name,
      email: email,
      body: message,
      subject: subject,
      recaptchaToken: recaptchaToken
    };
    try {
      const response = await FetchService.SendEmailContactUs(request);
      setSending(false);
      if (response) {
        setSuccessMessage("Din melding er sendt");
        setName("");
        setEmail("");
        setMessage("");
        setSubject("");
        setRecaptchaCompleted(false);
        if (window.grecaptcha && recaptchaWidgetRef.current) {
          window.grecaptcha.reset(recaptchaWidgetRef.current.dataset.widgetId);
        }
      } else {
        setErrorMessage("Feil. Vær så snill, prøv på nytt");
      }
    } catch (err) {
      setErrorMessage("Noe gikk galt. Vennligst prøv igjen senere.");
      setSending(false);
    }
  };

  return (
    <section className="Contact_main">
      <div className="container">
        <div className="row row_custom">
          <div className="col-lg-6 col_content_otr">
            <div className="col_content_inr">
              <ul className="icon_ul">
                <li className="icon_li">
                  <Link to="mailto:support@funtlers.com" className="icon_text_otr">
                    <div className="Icon_otr">
                      <i className="ri-mail-fill Icon"></i>
                    </div>
                    <p className="filter_heading text">support@funtlers.com</p>
                  </Link>
                </li>
                <li className="icon_li">
                  <Link to="tel:+930 276 367" className="icon_text_otr">
                    <div className="Icon_otr">
                      <i className="ri-phone-fill Icon"></i>
                    </div>
                    <p className="filter_heading text">930 276 367</p>
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="col-lg-6 col_form_otr">
            <form onSubmit={handleSubmit} className="col_form_inr" autoComplete="off">
              <div className="input_otr">
                <Input
                  InputClass="Theme_input_white filter_heading"
                  Inputype="name"
                  InputName="name"
                  InputPlaceholder="Navn"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  required
                />
              </div>
              <div className="input_otr">
                <Input
                  InputClass="Theme_input_white filter_heading"
                  Inputype="email"
                  InputName="email"
                  InputPlaceholder="Epost"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                />
              </div>
              <div className="input_otr">
                <Input
                  InputClass="Theme_input_white filter_heading"
                  Inputype="text"
                  InputName="text"
                  InputPlaceholder="Emne"
                  value={subject}
                  onChange={(event) => setSubject(event.target.value)}
                  required
                />
              </div>
              <div className="input_otr">
                <textarea
                  className="Theme_input_white Text_area filter_heading"
                  placeholder="Melding"
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  minLength={6}
                  required
                />
              </div>
              <div className="mb-3">
                <div
                  ref={recaptchaWidgetRef}
                  className="g-recaptcha"
                  data-sitekey={RECAPTCHA_SITE_KEY}
                />
              </div>
              {errorMessage && (
                <div className="error_message mb-2" role="alert">
                  {errorMessage}
                </div>
              )}
              {successMessage && (
                <div className="success_message mb-2" role="status">
                  {successMessage}
                </div>
              )}
              <div className="action_otr d-flex justify-content-center">
                <button
                  type="submit"
                  className="Theme_btn_primary send_btn"
                  disabled={!isFormValid()}
                  aria-busy={sending}
                  aria-disabled={!isFormValid()}
                >
                  {sending ? (
                    <span>
                      <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                      Sender...
                    </span>
                  ) : (
                    "Send"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ContactSection;